import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { Calendar, TrendingUp, Hash, FileDown, Loader2 } from 'lucide-react';
import TopRibbon from '@/components/TopRibbon';
import { QuickControlsCollapsible } from '@/components/sizing/QuickControlsCollapsible';
import { SizingSummaryDashboard } from '@/components/sizing/SizingSummaryDashboard';
import { LoadingScreen } from '@/components/layout/LoadingScreen';
import ErrorPanel from '@/components/outputs/ErrorPanel';
import { NarrativePanels } from '@/components/outputs/NarrativePanels';
import { useCatalog } from '@/hooks/useCatalog';
import { usePersonaCatalog, usePersonaProfiles } from '@/hooks/usePersonaCatalog';
import { useWizardState } from '@/hooks/useWizardState';
import { useSizingEngineV2 } from '@/hooks/useSizingEngineV2';
import { buildMcRequest } from '@/sizing/buildMcRequest';
import { useNewSizingNarrative } from '@/hooks/useNewSizingNarrative';
import type { NewSizingRequest } from '@/sizing/newSizingEngine';
import { TERM_CONCURRENT } from '@/lib/terms';
import { SCENARIO_PRESETS } from '@/hooks/useSizingStateV2';
import { resolveEffectivePersona } from '@/lib/personaResolver';
import type { PersonaProfile } from '@/types/personas';
import { Button } from '@/components/ui/button';
import { generateTextBasedPdf, type PdfReportData } from '@/lib/pdfReportBuilder';
import { toast } from '@/hooks/use-toast';

const BYTES_PER_GIB = 1024 ** 3;

const SizingPage = () => {
  const {
    catalog,
    isLoading: catalogLoading,
    error,
    catalogCommitted,
    catalogHash,
    committedThroughputCount,
    committedModelsCount,
  } = useCatalog();
  const { state: wizardState } = useWizardState();
  const catalogReady = catalog?.ready ?? false;

  const personaProfilesQuery = usePersonaProfiles();
  const personaProfiles = personaProfilesQuery.data ?? [];

  const sizing = useSizingEngineV2({
    personaProfiles: personaProfilesQuery.data ?? [],
  });

  const personaCatalog = usePersonaCatalog({
    enabled: !catalogLoading && !error,
  });

  const { snapshot, compute, loading } = sizing;

  // PDF export state
  const [isExportingPdf, setIsExportingPdf] = useState(false);

  console.log('[SizingPage] Unified sizing state', {
    status: sizing.status ?? 'unknown',
    hasSnapshot: Boolean(snapshot),
    concurrentUsers: sizing.scenario.tradeoffs.concurrentUsers,
    usersCount: sizing.scenario.users.length,
    catalogReady: !catalogLoading && !!catalog,
    catalogCommitted,
    catalogHash,
    personaCatalogReady: !personaCatalog.isLoading && !!personaCatalog.data,
    profilesReady: !personaProfilesQuery.isLoading && !!personaProfilesQuery.data,
  });
  console.log('[SizingPage] Personas query state', {
    isLoading: personaProfilesQuery.isLoading,
    isError: personaProfilesQuery.isError,
    error: personaProfilesQuery.error?.message,
    count: personaProfiles.length ?? 0,
    ids: personaProfiles.map((p) => p.id),
  });

  const toNum = (value: unknown): number | null => {
    const n = typeof value === 'number' ? value : Number(value);
    return Number.isFinite(n) ? n : null;
  };

  const parseJson = <T,>(value: unknown): T | null => {
    if (!value) return null;
    try {
      return typeof value === 'string' ? (JSON.parse(value) as T) : (value as T);
    } catch {
      return null;
    }
  };

  const resolvePersonaLatency = (
    profile: PersonaProfile | undefined,
    mode: 'snappy' | 'fast' | 'resp' | 'comfy' | 'async' | 'responsive',
  ) => {
    if (!profile) {
      return { ttftSec: 0, decodeTokPerSec: 0 };
    }

    const key = mode === 'responsive' ? 'resp' : mode;

    const ttftMsByKey: Record<'snappy' | 'fast' | 'resp' | 'comfy' | 'async', number | null> = {
      snappy: toNum((profile as any).ttft_snappy_ms),
      fast: toNum((profile as any).ttft_fast_ms),
      resp: toNum((profile as any).ttft_resp_ms),
      comfy: toNum((profile as any).ttft_comfy_ms),
      async: toNum((profile as any).ttft_async_ms),
    };

    const decodeTpsByKey: Record<'snappy' | 'fast' | 'resp' | 'comfy' | 'async', number | null> = {
      snappy: toNum((profile as any).decode_tps_snappy),
      fast: toNum((profile as any).decode_tps_fast),
      resp: toNum((profile as any).decode_tps_resp),
      comfy: toNum((profile as any).decode_tps_comfy),
      async: toNum((profile as any).decode_tps_async),
    };

    const ttftMs =
      ttftMsByKey[key] ??
      toNum(profile.default_latency_slo_ms) ??
      0;

    const decodeTokPerSec = decodeTpsByKey[key] ?? 0;

    const ttftSec = ttftMs / 1000;

    return { ttftSec, decodeTokPerSec };
  };

  // Derive latency mode and effective batch from the response speed slider
  const tolerance = sizing.scenario.tradeoffs.latencyTolerance ?? 'balanced';

  const slaMode = (() => {
    switch (tolerance) {
      case 'aggressive':
        return 'snappy' as const;
      case 'fast':
        return 'fast' as const;
      case 'balanced':
        return 'responsive' as const;
      case 'relaxed':
        return 'comfy' as const;
      case 'queue':
      case 'none':
      default:
        return 'async' as const;
    }
  })();

  const defaultBatchForTolerance: Record<string, number> = {
    aggressive: 1,
    fast: 4,
    balanced: 16,
    relaxed: 64,
    queue: 256,
    none: 256,
  };

  const effectiveBatch =
    defaultBatchForTolerance[tolerance] ?? defaultBatchForTolerance.balanced;

  const mcLatencyMode = slaMode === 'responsive' ? 'resp' : slaMode;

  const throughputEntry = useMemo(() => {
    console.log('[SizingPage] throughput memo check', {
      hasCatalog: !!catalog,
      catalogReady,
      catalogCommitted,
      catalogHash,
      throughputCount: catalog?.throughput?.length ?? 0,
      committedThroughputCount,
      committedModelsCount,
      modelId: sizing.scenario.selectedModelId ?? catalog?.models?.[0]?.model_id,
    });

    if (!catalogCommitted || !catalogReady) return null;
    if (!catalog?.throughput?.length) return null;
    const modelId = sizing.scenario.selectedModelId ?? catalog.models?.[0]?.model_id;
    if (!modelId) return null;

    const precisionBits = Number(sizing.scenario.tradeoffs.precision ?? 8);
    const backend = catalog.accelerator?.backend ?? null;
    const batchTarget = effectiveBatch;
    const throughputPoints = catalog.throughput;

    // First, filter to exact model + precision (+ backend if present)
    const primaryCandidates = throughputPoints.filter((point) => {
      if (point.model_id !== modelId) return false;
      if (Number(point.precision) !== precisionBits) return false;
      if (backend && (point.backend ?? backend) !== backend) return false;
      return true;
    });

    let chosen = null as (typeof throughputPoints)[number] | null;

    if (primaryCandidates.length > 0) {
      const sorted = [...primaryCandidates].sort((a, b) => (a.batch ?? 0) - (b.batch ?? 0));
      chosen =
        sorted.find((p) => (p.batch ?? 0) >= batchTarget) ??
        sorted[sorted.length - 1];
    }

    // Fallback without precision match but still same model and backend
    if (!chosen) {
      const modelBackendCandidates = throughputPoints.filter((point) => {
        if (point.model_id !== modelId) return false;
        if (backend && (point.backend ?? backend) !== backend) return false;
        return true;
      });
      if (modelBackendCandidates.length > 0) {
        const sorted = [...modelBackendCandidates].sort(
          (a, b) => (a.batch ?? 0) - (b.batch ?? 0),
        );
        chosen =
          sorted.find((p) => (p.batch ?? 0) >= batchTarget) ??
          sorted[sorted.length - 1];
      }
    }

    // Last resort: any row for this model
    if (!chosen) {
      const anyModel = throughputPoints.filter((point) => point.model_id === modelId);
      if (anyModel.length > 0) {
        const sorted = [...anyModel].sort((a, b) => (a.batch ?? 0) - (b.batch ?? 0));
        chosen =
          sorted.find((p) => (p.batch ?? 0) >= batchTarget) ??
          sorted[sorted.length - 1];
      }
    }

    // Final fallback: first entry in table
    chosen = chosen ?? throughputPoints[0] ?? null;

    if (chosen) {
      console.log('[SizingPage] selected throughput entry', {
        modelId,
        precisionBits,
        backend,
        batchTarget,
        chosenId: (chosen as any).id,
        chosenBatch: chosen.batch,
        chosenPromptTps: (chosen as any).prompt_tps,
        chosenDecodeTps: (chosen as any).decode_tps,
      });
    } else {
      console.log('[SizingPage] no throughput entry selected', {
        modelId,
        precisionBits,
        backend,
        batchTarget,
      });
    }

    return chosen;
  }, [catalog, catalogCommitted, catalogHash, sizing.scenario.selectedModelId, effectiveBatch, sizing.scenario.tradeoffs.precision]);

  // Build unified sizing request when inputs are ready
  const usersCount = sizing.scenario.users.length;
  const personaCatalogReady = !personaCatalog.isLoading && !!personaCatalog.data;
  const profilesReady = !personaProfilesQuery.isLoading && !!personaProfilesQuery.data;

  const sizingRequest = useMemo<NewSizingRequest | null>(() => {
    console.log('[SizingPage] sizingRequest guard', {
      catalogLoading,
      catalogReady,
      catalogCommitted,
      catalogHash,
      hasCatalog: !!catalog,
      hasPersonaCatalog: personaCatalogReady,
      profilesReady,
      hasThroughputEntry: !!throughputEntry,
      concurrentUsers: sizing.scenario.tradeoffs.concurrentUsers,
      usersCount,
    });
    if (catalogLoading) return null;
    if (!catalogReady || !catalogCommitted) return null;
    if (!catalog || !personaCatalogReady || !throughputEntry || !profilesReady) return null;

    const concurrentUsers = sizing.scenario.tradeoffs.concurrentUsers ?? 0;
    if (concurrentUsers <= 0) return null;
    if (usersCount <= 0) return null;

    const profilesById: Record<string, PersonaProfile> = Object.fromEntries(
      personaProfiles.map((p) => [p.id, p]),
    );
    console.log('[SizingPage] profiles map ready', {
      count: Object.keys(profilesById).length,
      keys: Object.keys(profilesById),
      first: profilesById[Object.keys(profilesById)[0] ?? ''],
    });

    const scenarioUsers = sizing.scenario.users;
    const personaCount = scenarioUsers.length || 1;
    console.log('[SizingPage] scenario users for effective personas', {
      count: scenarioUsers.length,
      users: scenarioUsers.map((u) => ({
        uid: u.uid,
        id: u.id,
        profileId: u.profileId,
        pct: u.pct,
        inTok: u.inTok,
        outTok: u.outTok,
        read_sec: u.read_sec,
        digest_sec: u.digest_sec,
      })),
    });

    const turns = sizing.scenario.tradeoffs.turns ?? 1;

    const effectivePersonas = scenarioUsers.map((user) =>
      resolveEffectivePersona(user, profilesById, slaMode, personaCount),
    );
    console.log('[SizingPage] effective personas', effectivePersonas);

    const personas = effectivePersonas.map((persona, idx) => {
      const user = scenarioUsers[idx];
      const profile =
        user?.profileId && user.profileId !== 'custom'
          ? profilesById[user.profileId]
          : undefined;

      const population = concurrentUsers * (persona.sharePercent / 100);
      const personaLatencyMode = persona.latencyMode ?? slaMode;
      const latencyKey = personaLatencyMode === 'responsive' ? 'resp' : personaLatencyMode;

      // pick the appropriate end to end target SLO from the profile
      const effectiveLatencyMs =
        profile
          ? (() => {
        switch (latencyKey) {
          case 'snappy':
            return toNum((profile as any).e2e_target_snappy_ms);
          case 'fast':
            return toNum((profile as any).e2e_target_fast_ms);
          case 'resp':
            return toNum((profile as any).e2e_target_resp_ms);
          case 'comfy':
            return toNum((profile as any).e2e_target_comfy_ms);
          case 'async':
            return toNum((profile as any).e2e_target_async_ms);
          default:
            return null;
        }
      })()
      : null;

      const inputTokensPerConversation = persona.inputTokensPerTurn * turns;
      const outputTokensPerConversation = persona.outputTokensPerTurn * turns;
      const { ttftSec, decodeTokPerSec } = resolvePersonaLatency(profile, latencyKey);

      const streamSec =
        decodeTokPerSec > 0 ? outputTokensPerConversation / decodeTokPerSec : 0;
      const digestSec = ttftSec + streamSec;

      const latency_profiles = profile
        ? {
            snappy: {
              ttft_ms: toNum((profile as any).ttft_snappy_ms) ?? 0,
              decode_tps: toNum((profile as any).decode_tps_snappy) ?? 0,
            },
            fast: {
              ttft_ms: toNum((profile as any).ttft_fast_ms) ?? 0,
              decode_tps: toNum((profile as any).decode_tps_fast) ?? 0,
            },
            resp: {
              ttft_ms: toNum((profile as any).ttft_resp_ms) ?? 0,
              decode_tps: toNum((profile as any).decode_tps_resp) ?? 0,
            },
            comfy: {
              ttft_ms: toNum((profile as any).ttft_comfy_ms) ?? 0,
              decode_tps: toNum((profile as any).decode_tps_comfy) ?? 0,
            },
            async: {
              ttft_ms: toNum((profile as any).ttft_async_ms) ?? 0,
              decode_tps: toNum((profile as any).decode_tps_async) ?? 0,
            },
          }
        : undefined;

      return {
        id: persona.id ?? `persona-${idx}`,
        name: persona.display_name ?? `Persona ${idx + 1}`,
        display_name: persona.display_name ?? `Persona ${idx + 1}`,
        prompt_tokens_per_turn: persona.inputTokensPerTurn,
        response_tokens_per_turn: persona.outputTokensPerTurn,
        turns_kept: turns,
        read_sec_default: persona.readSec,
        think_sec_default: persona.thinkSec,
        sharePercent: persona.sharePercent,
        population,
        inputTokensPerConversation,
        outputTokensPerConversation,
        readSeconds: persona.readSec,
        thinkSeconds: persona.thinkSec,
        ttftMs: ttftSec * 1000,
        decodeTps: decodeTokPerSec,
        turnsKept: turns,
        latencyTargetMs: effectiveLatencyMs,
        tokens: {
          input: persona.inputTokensPerTurn,
          output: persona.outputTokensPerTurn,
        },
        readSec: persona.readSec,
        thinkSec: persona.thinkSec,
        digestSec,
        turnsPerSession: turns,
        latency_profiles,
        latency_slo_default_ms: profile?.default_latency_slo_ms ?? 0,
        profileId: user?.profileId,
      };
    });
    console.log('[SizingPage] personas payload for MC', {
      latencyMode: mcLatencyMode,
      personas: personas.map((p) => ({
        id: p.id,
        profileId: p.profileId,
        sharePercent: p.sharePercent,
        in: p.inputTokensPerConversation,
        out: p.outputTokensPerConversation,
        read: p.readSeconds,
        think: p.thinkSeconds,
        ttftMs: p.ttftMs,
        decodeTps: p.decodeTps,
      })),
    });

    const selectedModelId =
      sizing.scenario.selectedModelId ?? catalog.models?.[0]?.model_id ?? 'model';

    // Ensure we bind to the actual catalog row (Supabase uses model_id, not an auto id)
    const modelRow =
      catalog.models?.find((m) => m.model_id === selectedModelId) ??
      catalog.models?.find((m) => m.model_id === catalog.models?.[0]?.model_id);

    const precisionBits = Number(sizing.scenario.tradeoffs.precision ?? 8);

    let kvBytesPerToken = 0;
    let weightsGiB = 0;
    let weightsGiBUnshared = 0;
    let runtimeOverheadGiB = 0;
    let usableKvGiBPerGpu = 0;
    let hbmGiB = 0;
    let fitsWeightsWithoutSharing = false;
    let fitsWeightsWithSharing = false;
    let bridgeGroupSize = 1;
    let weightsSharedWithinGroup = false;

    if (modelRow && catalog.accelerator) {
      const kvMap = parseJson<Record<string, number>>(modelRow.kv_per_token_json) ?? {};
      const weightsMap =
        parseJson<Record<string, number>>(modelRow.weights_mem_json) ?? {};

      kvBytesPerToken = kvMap[String(precisionBits)] ?? 0;

      const accel = catalog.accelerator as any;

      bridgeGroupSize = Number(accel.bridge_group_size_max ?? 1) || 1;
      weightsSharedWithinGroup = true;

      const weightsBytesRaw = weightsMap[String(precisionBits)] ?? 0;

      weightsGiBUnshared = weightsBytesRaw / BYTES_PER_GIB;

      const weightsBytesPerGpu =
        weightsSharedWithinGroup && bridgeGroupSize > 0
          ? weightsBytesRaw / bridgeGroupSize
          : weightsBytesRaw;

      weightsGiB = weightsBytesPerGpu / BYTES_PER_GIB;

      const hbmB = Number(accel.hbm_bytes ?? 0);
      hbmGiB = hbmB / BYTES_PER_GIB;

      const modelRuntimeBytes = Number(modelRow.base_runtime_overhead_bytes ?? 0);
      const reservedRuntimeBytesPerGpu = Number(accel.reserved_runtime_bytes ?? 0);
      const runtimeGroupBytes = Number(accel.runtime_overhead_bytes_per_group ?? 0);
      const runtimeGroupBytesPerGpu =
        bridgeGroupSize > 0 ? runtimeGroupBytes / bridgeGroupSize : runtimeGroupBytes;

      const totalRuntimeBytesPerGpu =
        modelRuntimeBytes + reservedRuntimeBytesPerGpu + runtimeGroupBytesPerGpu;

      runtimeOverheadGiB = totalRuntimeBytesPerGpu / BYTES_PER_GIB;

      fitsWeightsWithoutSharing =
        hbmGiB > 0 ? weightsGiBUnshared + runtimeOverheadGiB <= hbmGiB : false;

      fitsWeightsWithSharing =
        hbmGiB > 0 ? weightsGiB + runtimeOverheadGiB <= hbmGiB : false;

      usableKvGiBPerGpu = Math.max(0, hbmGiB - weightsGiB - runtimeOverheadGiB);

      console.log('[SizingPage] model KV caps', {
        modelId: selectedModelId,
        precisionBits,
        kvBytesPerToken,
        hbmGiB,
        weightsGiBUnshared,
        weightsGiB,
        runtimeOverheadGiB,
        usableKvGiBPerGpu,
        fitsWeightsWithoutSharing,
        fitsWeightsWithSharing,
        bridgeGroupSize,
        weightsSharedWithinGroup,
      });
    }

    const model = {
      id: modelRow?.model_id ?? selectedModelId,
      paramsBillions: Number(modelRow?.params_b ?? 0),
      precisionBits,
      kvBytesPerToken,
      weightsGiB,
      usableKvGiBPerGpu,
      hbmGiB,
      weightsGiBUnshared,
      fitsWeightsWithoutSharing,
      fitsWeightsWithSharing,
      bridgeGroupSize,
      weightsSharedWithinGroup,
    };

    const throughput = {
      promptTpsPerGpu: Number(
        (throughputEntry as any).prompt_tps ??
          (throughputEntry as any).promptTps ??
          0,
      ),
      decodeTpsPerGpu: Number(
        (throughputEntry as any).decode_tps ??
          (throughputEntry as any).decodeTps ??
          0,
      ),
    };

    console.log('[SizingPage] throughput for request', {
      modelId: selectedModelId,
      precisionBits,
      batch: effectiveBatch,
      promptTpsPerGpu: throughput.promptTpsPerGpu,
      decodeTpsPerGpu: throughput.decodeTpsPerGpu,
    });

    const uiState = {
      conversationTurns: sizing.scenario.tradeoffs.turns ?? 3,
      batchSize: effectiveBatch,
      latencySloMs:
        sizing.scenario.advanced.latencyMs ??
        sizing.scenario.advanced.latencyTargetMs ??
        0,
      mcFastTrials: 8,
      mcRefineTrials: 32,
      mcHorizonSeconds: 600,
      trafficVolatility: 0.2,
    };

    const req = buildMcRequest({
      personas,
      model,
      throughput,
      uiState,
      scenario: { concurrentUsers },
      latencyMode: mcLatencyMode,
    });
    if (!req) {
      console.log('[SizingPage] Skipping sizing - no personas in scenario');
      return null;
    }
    console.log('[SizingPage] built sizing request', {
      personas: req.personas?.length ?? 0,
      concurrentUsers: req.baseConcurrentUsers,
      modelId: req.model.id,
      promptTpsPerGpu: req.throughput.promptTpsPerGpu,
      decodeTpsPerGpu: req.throughput.decodeTpsPerGpu,
    });
    return req;
  }, [
    catalog,
    catalogHash,
    catalogCommitted,
    catalogLoading,
    personaCatalog.data,
    personaCatalogReady,
    throughputEntry,
    personaProfiles,
    profilesReady,
    sizing.scenario.selectedModelId,
    sizing.scenario.tradeoffs.concurrentUsers,
    sizing.scenario.tradeoffs.turns,
    sizing.scenario.tradeoffs.latencyTolerance,
    sizing.scenario.advanced.latencyMs,
    sizing.scenario.advanced.latencyTargetMs,
    sizing.scenario.users,
    usersCount,
    effectiveBatch,
    slaMode,
    mcLatencyMode,
    catalogCommitted,
    catalogHash,
  ]);

  useEffect(() => {
    if (!sizingRequest) return;
    console.log('[SizingPage] triggering unified compute', {
      modelId: sizingRequest.model.id,
      precisionBits: sizingRequest.model.precisionBits,
      usersCount,
      concurrentUsers: sizing.scenario.tradeoffs.concurrentUsers,
      catalogCommitted,
      catalogHash,
      throughput: {
        batch: sizingRequest.batchSize,
        promptTpsPerGpu: sizingRequest.throughput.promptTpsPerGpu,
        decodeTpsPerGpu: sizingRequest.throughput.decodeTpsPerGpu,
      },
    });
    compute(sizingRequest);
  }, [compute, sizingRequest, sizing.scenario.tradeoffs.concurrentUsers, catalogCommitted, catalogHash, usersCount]);

  const heroKpis = useMemo(() => {
    if (!snapshot) {
      return [
        {
          label: TERM_CONCURRENT,
          value: sizing.scenario.tradeoffs.concurrentUsers ?? '-',
          icon: Calendar,
        },
        { label: "XE7740's Needed", value: '-', icon: TrendingUp },
        { label: 'Tokens / Month', value: '-', icon: Hash },
      ];
    }

    return [
      {
        label: TERM_CONCURRENT,
        value: sizing.scenario.tradeoffs.concurrentUsers ?? 0,
        icon: Calendar,
      },
      {
        label: "XE7740's Needed",
        value: snapshot.finalGpuCount ?? 0,
        icon: TrendingUp,
      },
      { label: 'Tokens / Month', value: '-', icon: Hash },
    ];
  }, [snapshot, sizing.scenario.tradeoffs.concurrentUsers]);

  const narrativeData = useNewSizingNarrative({
    snapshot,
    modelName: sizingRequest?.model.id,
    serverSku: 'XE7740',
    serverDisplayName: 'Dell XE7740',
    gpusPerServer: 8,
    hbmBytesPerGpu: (sizingRequest?.model.hbmGiB ?? 96) * BYTES_PER_GIB,
    weightsGiB: sizingRequest?.model.weightsGiB,
    weightsGiBUnshared:
      sizingRequest?.model.weightsGiBUnshared ?? sizingRequest?.model.weightsGiB,
    runtimeOverheadGiB: sizingRequest
      ? (sizingRequest.model.hbmGiB ?? 96) -
        sizingRequest.model.weightsGiB -
        sizingRequest.model.usableKvGiBPerGpu
      : undefined,
    usableKvGiBPerGpu: sizingRequest?.model.usableKvGiBPerGpu,
    bridgeGroupSize: sizingRequest?.model.bridgeGroupSize ?? 1,
    conversationTurns: sizingRequest?.conversationTurns,
    latencyMs: sizingRequest?.latencySloMs,
    batchSize: sizingRequest?.batchSize,
    baseConcurrentUsers: sizingRequest?.baseConcurrentUsers,
    personas: sizingRequest?.personas,
  });

  // PDF Export handler - using text-based PDF generation
  const handleExportPdf = useCallback(async () => {
    if (!snapshot || !sizingRequest) {
      toast({
        title: 'Unable to Export',
        description: 'Please wait for sizing results to load.',
        variant: 'destructive',
      });
      return;
    }

    setIsExportingPdf(true);

    try {
      const pdfData: PdfReportData = {
        snapshot,
        request: sizingRequest,
        narrativeData: narrativeData ?? undefined,
        serverDisplayName: catalog?.server?.display_name ?? 'Dell XE7740',
        acceleratorDisplayName: catalog?.accelerator?.display_name ?? 'Intel Gaudi 3',
      };

      await generateTextBasedPdf(pdfData);

      toast({
        title: 'Report Exported',
        description: 'Your sizing report has been downloaded as PDF.',
      });
    } catch (err) {
      console.error('[SizingPage] PDF export error:', err);
      toast({
        title: 'Export Failed',
        description: 'Unable to generate PDF. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsExportingPdf(false);
    }
  }, [snapshot, sizingRequest, catalog]);

  if (catalogLoading || personaCatalog.isLoading || personaProfilesQuery.isLoading) {
    return <LoadingScreen message="Loading catalog..." />;
  }

  if (error || personaCatalog.error || personaProfilesQuery.error) {
    const err =
      (error as Error) ??
      (personaCatalog.error as Error) ??
      (personaProfilesQuery.error as Error);
    return (
      <ErrorPanel
        title="Catalog Load Failed"
        message="Unable to load sizing catalog. Please try again."
        error={err}
      />
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <TopRibbon />

      <div className="space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-foreground">
              Plan Your On-Prem Inference Capacity
            </h1>
            <p className="text-muted-foreground">
              Configure workload parameters and see infrastructure requirements
            </p>
          </div>
          <Button
            onClick={handleExportPdf}
            disabled={!snapshot || !sizingRequest || isExportingPdf}
            className="shrink-0 gap-2"
            variant="outline"
          >
            {isExportingPdf ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Generating PDF...
              </>
            ) : (
              <>
                <FileDown className="h-4 w-4" />
                Export PDF Report
              </>
            )}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3 items-start">
        <div className="lg:col-span-1">
          <div className="sticky top-6 self-start">
            <QuickControlsCollapsible
              tradeoffs={sizing.scenario.tradeoffs}
              users={sizing.scenario.users}
              totalPct={sizing.scenario.totalPct}
              warningUnderPct={sizing.scenario.warningUnderPct}
              blockedOverPct={sizing.scenario.blockedOverPct}
              catalog={catalog}
              catalogLoading={catalogLoading}
              selectedModelId={sizing.scenario.selectedModelId}
              onModelChange={sizing.setSelectedModelId}
              onTradeoffChange={sizing.setTradeoff}
              onUserUpdate={sizing.updateUser}
              onUserRemove={sizing.removeUser}
              onUserAdd={sizing.addUser}
              advanced={sizing.scenario.advanced}
              onAdvancedChange={sizing.setAdvanced}
              sticky={false}
              presets={SCENARIO_PRESETS}
              onPresetSelect={sizing.applyPreset}
            />
          </div>
        </div>

        <div className="lg:col-span-2 space-y-6">
          <SizingSummaryDashboard
            snapshot={snapshot}
            serverCount={snapshot ? Math.ceil(snapshot.finalGpuCount / 8) : 0}
            gpusPerServer={8}
            serverDisplayName={catalog?.server?.display_name ?? 'XE7740 Servers'}
            acceleratorDisplayName={
              catalog?.accelerator?.display_name ?? 'Intel Gaudi 3 Accelerators'
            }
          />

          {snapshot && sizingRequest && (
            <NarrativePanels
              snapshot={snapshot}
              modelName={sizingRequest.model.id}
              serverSku="XE7740"
              serverDisplayName="Dell XE7740"
              gpusPerServer={8}
              hbmBytesPerGpu={(sizingRequest.model.hbmGiB ?? 96) * BYTES_PER_GIB}
              weightsGiB={sizingRequest.model.weightsGiB}
              weightsGiBUnshared={
                sizingRequest.model.weightsGiBUnshared ?? sizingRequest.model.weightsGiB
              }
              runtimeOverheadGiB={
                (sizingRequest.model.hbmGiB ?? 96) -
                sizingRequest.model.weightsGiB -
                sizingRequest.model.usableKvGiBPerGpu
              }
              usableKvGiBPerGpu={sizingRequest.model.usableKvGiBPerGpu}
              bridgeGroupSize={sizingRequest.model.bridgeGroupSize ?? 1}
              conversationTurns={sizingRequest.conversationTurns}
              latencyMs={sizingRequest.latencySloMs}
              batchSize={sizingRequest.batchSize}
              baseConcurrentUsers={sizingRequest.baseConcurrentUsers}
              personas={sizingRequest.personas}
              isComputing={loading}
            />
          )}
        </div>
      </div>

    </div>
  );
};

export default SizingPage;
